import { Box, useTheme } from "@mui/material";
import * as React from "react";
import { Preferences } from "../../hooks/useUserPreferences";
import {
  drawBayBoundaries,
  drawSpacingBuffers,
} from "../../screens/Settings/HangarSettings/ObstacleCanvasDrawers";
import { Hangar } from "../../types";
import Canvas from "../useCanvas";

type Props = {
  selectable: boolean;
  width: number;
  height: number;
  hangar: Hangar;
  feetToPixels: number;
  preferences?: Preferences;
  backgroundColor: string;
  dashColor: string;
  borderColor?: string;
  onClick: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseDown: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseUp: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseMove: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter: (evt: React.MouseEvent<HTMLElement>) => void;
  onContextMenu: (evt: React.MouseEvent<HTMLElement>) => void;
  children?: React.ReactNode;
};

export const HangarLayoutBackground = React.forwardRef((props: Props, ref) => {
  const {
    width,
    height,
    hangar,
    feetToPixels,
    preferences,
    onClick,
    onMouseDown,
    onMouseUp,
    onMouseMove,
    onMouseEnter,
    onContextMenu,
    ...otherProps
  } = props;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const gridSize = (width > 600 ? 10 : 10) * feetToPixels;

  const dashLength = gridSize / 10;
  const dashWidth = dashLength / 4;
  const backgroundColor = otherProps.backgroundColor ??
    (isDarkMode ? theme.palette.background.default : "#c8cfde");
  const borderColor = otherProps.borderColor ??
    (isDarkMode ? theme.palette.divider : "#7a94bb");
  const dashColor = otherProps.dashColor ??
    (isDarkMode ? "#5a6775" : "#7a94bb");
  const canvasRef = React.createRef<HTMLCanvasElement>();
  const draw = React.useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // Clear the canvas (accounting for the 0.5 pixel translation)
      ctx.clearRect(-1, -1, width + 2, height + 2);
      drawSpacingBuffers(ctx, hangar, (x) => x * feetToPixels, {
        lineWidth: width > 600 ? 1.5 : 1,
        lineDash: width > 600 ? null : [dashLength * 2, dashLength * 2],
        showWallSpacingFill: preferences?.showWallSpacingFill ?? false,
      });

      // Draw bay boundaries if enabled
      if (preferences?.showBayBoundaries && hangar.bays?.length > 0) {
        drawBayBoundaries(ctx, hangar.bays, hangar, (x) => x * feetToPixels);
      }

      // Draw hangar name watermark in the center
      if (hangar.name) {
        ctx.save();

        // Calculate responsive font size based on canvas dimensions
        const minDimension = Math.min(width, height);
        let fontSize = Math.min(40, minDimension * 0.16);
        fontSize = Math.max(16, fontSize); // Ensure minimum readability

        ctx.font = `${fontSize}px "Lexend Deca", sans-serif`;

        // Measure text and scale down if it's too wide
        const textMetrics = ctx.measureText(hangar.name);
        const maxWidth = width * 0.8; // Use 80% of width
        if (textMetrics.width > maxWidth) {
          fontSize = fontSize * (maxWidth / textMetrics.width);
          ctx.font = `${fontSize}px "Lexend Deca", sans-serif`;
        }

        ctx.fillStyle = isDarkMode
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(51, 51, 51, 0.25)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(hangar.name, width / 2, height / 2);
        ctx.restore();
      }
    },
    [
      hangar,
      height,
      preferences?.showWallSpacingFill,
      preferences?.showBayBoundaries,
      width,
      feetToPixels,
      dashLength,
      isDarkMode,
    ]
  );

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        m: 2,
      }}
    >
      <Box
        data-id="hangar-layout-background"
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onMouseEnter={onMouseEnter}
        onContextMenu={onContextMenu}
        sx={{
          position: "absolute",
          // need to account for border here
          width: width,
          height: height,
        }}
      >
        {props.children}
      </Box>
      <Canvas
        ref={canvasRef}
        width={width}
        height={height}
        draw={draw}
        style={{
          outline: `2px solid ${borderColor}`,
          backgroundColor,
          backgroundSize: `${dashLength * 2}px ${
            dashLength * 2
          }px, ${gridSize}px ${gridSize}px`,
          backgroundPosition: `${-dashWidth / 2}px ${-dashWidth / 2}px`,
          backgroundImage: `linear-gradient(to bottom, transparent ${dashLength}px, ${backgroundColor} ${dashLength}px),
                linear-gradient(to right, ${dashColor}50 ${
            (dashLength * 3) / 5
          }px, transparent ${(dashLength * 3) / 5}px),
                linear-gradient(to right, transparent ${dashLength}px, ${backgroundColor} ${dashLength}px),
                linear-gradient(to bottom, ${dashColor}50 ${
            (dashLength * 3) / 5
          }px, transparent ${(dashLength * 3) / 5}px)
              `,
        }}
      />
    </Box>
  );
});
